import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GraduationCap } from "@phosphor-icons/react";

export function AuthLayout({ title, subtitle, children, testid }) {
    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid={testid}>
            <div className="hidden lg:block relative bg-ink text-bone paper-grain">
                <img
                    src="https://images.unsplash.com/photo-1585661417298-8236a5f449aa?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Study"
                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                />
                <div className="relative z-10 h-full flex flex-col p-12">
                    <Link to="/" className="flex items-center gap-2 self-start">
                        <span className="w-9 h-9 grid place-items-center bg-bone text-ink rounded-sm">
                            <GraduationCap size={20} weight="duotone" />
                        </span>
                        <div>
                            <div className="font-serif text-lg leading-none">Wibpro</div>
                            <div className="text-[10px] tracking-[0.28em] text-gold uppercase">Academy</div>
                        </div>
                    </Link>
                    <div className="mt-auto max-w-md">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Preface</div>
                        <p className="font-serif text-3xl leading-tight italic font-light">
                            "The mind, once stretched by a new idea, never returns to its original dimensions."
                        </p>
                        <div className="text-sm text-bone/60 mt-4">— O. W. Holmes</div>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <h1 className="font-serif text-4xl text-ink">{title}</h1>
                    <p className="text-ink/60 mt-2">{subtitle}</p>
                    <div className="mt-8">{children}</div>
                </div>
            </div>
        </div>
    );
}

export function LoginPage() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally { setLoading(false); }
    };

    return (
        <AuthLayout title="Welcome back" subtitle="Sign in to continue your preparation." testid="login-page">
            <form onSubmit={submit} className="space-y-5">
                <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" data-testid="input-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-1.5 rounded-sm" />
                </div>
                <div>
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" data-testid="input-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="mt-1.5 rounded-sm" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-login-submit">
                    {loading ? "Signing in…" : "Sign in"}
                </Button>
                <div className="text-sm text-ink/60 text-center">
                    New here? <Link to="/register" className="underline underline-offset-4 decoration-gold decoration-2 text-ink" data-testid="link-to-register">Create an account</Link>
                </div>
            </form>
        </AuthLayout>
    );
}

export function RegisterPage() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(name, email, password);
            toast.success("Welcome to Wibpro Academy");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not register");
        } finally { setLoading(false); }
    };

    return (
        <AuthLayout title="Enrol at Wibpro" subtitle="Free forever for the first practice test in every course." testid="register-page">
            <form onSubmit={submit} className="space-y-5">
                <div>
                    <Label htmlFor="name">Full name</Label>
                    <Input id="name" data-testid="input-name" value={name} onChange={e => setName(e.target.value)} required className="mt-1.5 rounded-sm" />
                </div>
                <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" data-testid="input-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-1.5 rounded-sm" />
                </div>
                <div>
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" data-testid="input-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} className="mt-1.5 rounded-sm" />
                    <div className="text-xs text-ink/50 mt-1">At least 8 characters.</div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-register-submit">
                    {loading ? "Creating account…" : "Create my account"}
                </Button>
                <div className="text-sm text-ink/60 text-center">
                    Already enrolled? <Link to="/login" className="underline underline-offset-4 decoration-gold decoration-2 text-ink" data-testid="link-to-login">Sign in</Link>
                </div>
            </form>
        </AuthLayout>
    );
}
